import React from 'react';

import { ClassName, I18nText, IsDisabled } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';

import { TaskNanoID } from '../../../tasksTypes';
import { ProjectNanoID } from '../../../../projects/projectsTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import {
  DropzoneHelperFileIds,
  DropzoneHelperFiles
} from '../../../../../helpers/dropzone/DropzoneHelper';

import { useSubmitResultsToTaskForm } from '../../forms/SubmitResultsToTaskForm/hooks/useSubmitResultsToTaskForm';

import { SubmitResultsToTaskForm } from '../../forms/SubmitResultsToTaskForm';
import { UploadedCounter } from './components/UploadedCounter';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { FormModalButton } from '../../../../../helpers/buttons/FormModalButton';

import { tasksKeys, words } from '../../../../../locales/keys';

interface SubmitResultsToTaskModalButtonProps {
  taskNanoId: TaskNanoID;
  className?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  initialFiles?: DropzoneHelperFiles;
  initialFileIds?: DropzoneHelperFileIds;
  i18nText?: I18nText;
  tooltipI18nText?: I18nText;
  projectNanoId?: ProjectNanoID;
  teamNanoId?: TeamNanoID;
  afterSubmit?: () => void;
  disabled?: IsDisabled;
}

const SUBMIT_TASK_RESULTS_FORM = 'submit-task-results-form';

function SubmitResultsToTaskModalButton({
  taskNanoId,
  className,
  icon,
  iconClassName,
  initialFiles,
  initialFileIds,
  i18nText,
  tooltipI18nText,
  projectNanoId,
  teamNanoId,
  afterSubmit,
  disabled
}: SubmitResultsToTaskModalButtonProps) {
  const {
    control,
    resetForm,
    handleSubmitResultsToTask,
    submitResultsToTaskErrorMessage,
    submitResultsToTaskLoading,
    submitResultsToTaskReset,
    registerHours,
    registerMinutes,
    registerAutoSubmit,
    validationErrors,
    allFiles,
    loadingFiles,
    handleChangeFiles
  } = useSubmitResultsToTaskForm({
    taskNanoId
  });

  return (
    <FormModalButton
      i18nText={i18nText}
      className={
        className ||
        'flex text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap'
      }
      form={SUBMIT_TASK_RESULTS_FORM}
      icon={icon}
      iconClassName={iconClassName}
      i18nSubmitText={words.submit}
      i18nTitle={tasksKeys.submitResults}
      tooltipI18nText={tooltipI18nText}
      isLoading={submitResultsToTaskLoading}
      onOpen={resetForm}
      submitDisabled={!!loadingFiles}
      onClose={submitResultsToTaskReset}
      onSubmit={handleSubmitResultsToTask}
      leftButtonsChildren={({ handleSubmit }) => (
        <UploadedCounter
          allFiles={allFiles}
          loadingFiles={loadingFiles}
          handleSubmit={handleSubmit}
          control={control}
        />
      )}
      afterSubmit={afterSubmit}
      disabled={disabled}
      closeOnOuterClick={false}
    >
      <SubmitResultsToTaskForm
        form={SUBMIT_TASK_RESULTS_FORM}
        control={control}
        isLoading={submitResultsToTaskLoading}
        registerHours={registerHours}
        registerMinutes={registerMinutes}
        registerAutoSubmit={registerAutoSubmit}
        fileAttachmentIdsValidationError={
          validationErrors.fileAttachmentIdsValidationError
        }
        hoursValidationError={validationErrors.hoursValidationError}
        minutesValidationError={validationErrors.minutesValidationError}
        initialFiles={initialFiles}
        initialFileIds={initialFileIds}
        onChangeFiles={handleChangeFiles}
        projectNanoId={projectNanoId}
        teamNanoId={teamNanoId}
        taskNanoId={taskNanoId}
      />

      <div className="px-4">
        <AlertMessage message={submitResultsToTaskErrorMessage} />
      </div>
    </FormModalButton>
  );
}

export default SubmitResultsToTaskModalButton;
